import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
// import images
import footer_logo from "@/assets/images/footer_logo.png";
import gift2 from "@/assets/images/gift2.jpg";
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setError("Invalid email or password. Please try again.");
    } else {
      navigate("/admin/dashboard");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Panel */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-cover bg-center relative"
        style={{
          backgroundImage:
            `url(${gift2})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/60" />
        <div className="relative z-10">
          <div className="flex items-center justify-center rounded-2xl bg-white/90 backdrop-blur-sm px-5 py-3 w-fit">
            <img
              src={footer_logo}
              alt="Gift Mantra"
              className="h-12 w-auto object-contain"
            />
          </div>
        </div>
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Manage Your Gift Empire
          </h2>
          <p className="text-white/70 text-base leading-relaxed max-w-sm">
            Full control over your products, categories, and curated collections — all in one beautiful dashboard.
          </p>
          <div className="flex flex-col gap-3 mt-8">
            {[
              { icon: "ri-store-2-line", text: "Manage Products & Categories" },
              { icon: "ri-star-line", text: "Feature & Highlight Products" },
              { icon: "ri-bar-chart-line", text: "Track your collection" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20">
                  <i className={`${item.icon} text-white text-sm`} />
                </div>
                <span className="text-white/80 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8 lg:hidden">
            <img
              src={footer_logo}
              alt="Gift Mantra"
              className="h-14 w-auto object-contain"
            />
          </div>

          <div className="mb-8">
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
              Admin Access
            </span>
            <h1 className="text-3xl font-bold text-gray-900 mt-2 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Welcome Back
            </h1>
            <p className="text-gray-500 text-sm">Sign in to manage your Gift Mantra store</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-12 py-3.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-800 placeholder-gray-400 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100">
                <i className="ri-error-warning-line text-red-500" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white font-semibold text-sm hover:opacity-90 disabled:opacity-70 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin" />Signing In...</>
              ) : (
                <><i className="ri-login-box-line" />Sign In to Dashboard</>
              )}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-8">
            This area is restricted to authorized personnel only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
